import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  MdAccountTree,
  MdAssessment,
  MdBusiness,
  MdGridOn,
  MdHandshake,
  MdClose,
  MdDescription,
  MdGroup,
  MdLaptopMac,
  MdListAlt,
  MdMap,
  MdNotificationsActive,
  MdPerson,
  MdSearch,
  MdSecurity,
  MdSettings,
  MdThumbUp,
  MdWarning,
} from "react-icons/md";

const normalize = (s) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

const match = (text, query) => {
  if (!query.trim()) return false;
  const q = normalize(query);
  const t = normalize(text);
  return t.includes(q) || q.split(/\s+/).every((w) => t.includes(w));
};

const PAGES = [
  { key: "dashboard", label: "Tableau de bord", icon: <MdAssessment size={16} /> },
  { key: "ropa", label: "ROPA — Registre des traitements", icon: <MdListAlt size={16} /> },
  { key: "ropa-flux", label: "Carte des flux — traitements", icon: <MdMap size={16} /> },
  { key: "consentement", label: "Consentements", icon: <MdThumbUp size={16} /> },
  { key: "preuves", label: "Preuves RGPD", icon: <MdDescription size={16} /> },
  { key: "referentiels", label: "Configuration / Référentiels", icon: <MdSettings size={16} /> },
  { key: "registre-sous-traitants", label: "Gouvernance — Registre sous-traitants", icon: <MdHandshake size={16} /> },
  { key: "matrice-raci", label: "Gouvernance — Matrice RACI", icon: <MdGridOn size={16} /> },
  { key: "pssi", label: "PSSI — Politique Sécurité", icon: <MdSecurity size={16} /> },
  { key: "inventaire-si", label: "Inventaire SI", icon: <MdLaptopMac size={16} /> },
  { key: "pra", label: "PRA — Plan Reprise", icon: <MdWarning size={16} /> },
  { key: "incidents", label: "Incidents & Violations", icon: <MdNotificationsActive size={16} /> },
  { key: "droits", label: "Droits des personnes", icon: <MdPerson size={16} /> },
  { key: "droits-config", label: "Config. processus Droits", icon: <MdSettings size={16} /> },
  { key: "societe-config", label: "Informations société", icon: <MdBusiness size={16} /> },
  { key: "team-config", label: "Equipe & invitations", icon: <MdGroup size={16} /> },
  { key: "reports-list", label: "Rapports — liste des rapports", icon: <MdDescription size={16} /> },
  { key: "reports-config", label: "Rapports — configuration des rapports", icon: <MdAccountTree size={16} /> },
];

export default function SearchCommandPalette({
  open,
  onClose,
  onSelect,
  searchData = {},
  mode,
}) {
  const [query, setQuery] = useState("");
  const [selectedIdx, setSelectedIdx] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  const {
    personnes = [],
    traitements = [],
    sousTraitants = [],
    pointsCollecte = [],
    retraits = [],
    incidents = [],
    droits = [],
    proofs = [],
    activites = [],
    elementsSI = [],
  } = searchData;

  const results = useMemo(() => {
    const q = query.trim();
    const out = [];

    if (!q) {
      out.push({ type: "section", label: "Pages" });
      PAGES.filter((p) => {
        if (mode === "express") {
          return ["dashboard", "ropa", "ropa-flux", "consentement", "droits", "droits-config", "societe-config"].includes(p.key);
        }
        return true;
      }).forEach((p) => out.push({ ...p, type: "page" }));
      return out;
    }

    const pageMatches = PAGES.filter(
      (p) =>
        match(p.label, q) ||
        match(p.key, q) ||
        match(p.key.replace("-", " "), q)
    ).filter((p) => {
      if (mode === "express") {
        return ["dashboard", "ropa", "ropa-flux", "consentement", "droits", "droits-config", "societe-config"].includes(p.key);
      }
      return true;
    });

    if (pageMatches.length > 0) {
      out.push({ type: "section", label: "Pages" });
      pageMatches.forEach((p) => out.push({ ...p, type: "page" }));
    }

    const personnesMatch = personnes.filter(
      (p) =>
        match(`${p.prenom || ""} ${p.nom || ""}`, q) ||
        match(p.email, q) ||
        match(p.fonction, q) ||
        match(p.habilitation, q)
    );
    if (personnesMatch.length > 0) {
      out.push({ type: "section", label: "Personnes référentiels" });
      personnesMatch.slice(0, 5).forEach((p) =>
        out.push({
          type: "personne",
          key: "referentiels",
          id: p.id,
          label: `${p.prenom || ""} ${p.nom || ""} ${p.email ? `(${p.email})` : ""}`.trim(),
          sub: p.habilitation || p.function,
        })
      );
    }

    const traitementsMatch = traitements.filter(
      (t) =>
        match(t.nom, q) ||
        match(t.description, q) ||
        match(t.finalite, q) ||
        match(t.baseLegale, q)
    );
    if (traitementsMatch.length > 0) {
      out.push({ type: "section", label: "Traitements (ROPA)" });
      traitementsMatch.slice(0, 5).forEach((t) =>
        out.push({
          type: "traitement",
          key: "ropa",
          id: t.id,
          label: t.nom,
          sub: t.finalite,
        })
      );
    }

    const sousMatch = sousTraitants.filter(
      (s) => match(s.nom, q) || match(s.pays, q) || match(s.typeService, q)
    );
    if (sousMatch.length > 0) {
      out.push({ type: "section", label: "Sous-traitants" });
      sousMatch.slice(0, 5).forEach((s) =>
        out.push({
          type: "sousTraitant",
          key: "registre-sous-traitants",
          id: s.id,
          label: s.nom,
          sub: s.pays,
        })
      );
    }

    const pointsMatch = pointsCollecte.filter(
      (p) => match(p.nom, q) || match(p.type, q) || match((p.finalites || []).join(" "), q)
    );
    if (pointsMatch.length > 0) {
      out.push({ type: "section", label: "Points de collecte consentement" });
      pointsMatch.slice(0, 5).forEach((p) =>
        out.push({
          type: "pointCollecte",
          key: "consentement",
          id: p.id,
          label: p.nom,
          sub: p.type,
        })
      );
    }

    const retraitsMatch = retraits.filter(
      (r) => match(r.personne, q) || match(r.canal, q) || match(r.statut, q)
    );
    if (retraitsMatch.length > 0) {
      out.push({ type: "section", label: "Retraits de consentement" });
      retraitsMatch.slice(0, 5).forEach((r) =>
        out.push({
          type: "retrait",
          key: "consentement",
          id: r.id,
          label: r.personne,
          sub: r.statut,
        })
      );
    }

    const incidentsMatch = incidents.filter(
      (i) =>
        match(i.titre, q) ||
        match(i.type, q) ||
        match(i.gravite, q) ||
        match(i.statut, q)
    );
    if (incidentsMatch.length > 0) {
      out.push({ type: "section", label: "Incidents" });
      incidentsMatch.slice(0, 5).forEach((i) =>
        out.push({
          type: "incident",
          key: "incidents",
          id: i.id,
          label: i.titre,
          sub: i.statut,
        })
      );
    }

    const droitsMatch = droits.filter(
      (d) => match(d.type, q) || match(d.description, q) || match(d.statut, q)
    );
    if (droitsMatch.length > 0) {
      out.push({ type: "section", label: "Demandes de droits" });
      droitsMatch.slice(0, 5).forEach((d) =>
        out.push({
          type: "droit",
          key: "droits",
          id: d.id,
          label: d.type,
          sub: d.statut,
        })
      );
    }

    const proofsMatch = proofs.filter(
      (p) =>
        match(p.intitule, q) ||
        match(p.description, q) ||
        match(p.categorie, q) ||
        match(p.statut, q)
    );
    if (proofsMatch.length > 0) {
      out.push({ type: "section", label: "Preuves RGPD" });
      proofsMatch.slice(0, 5).forEach((p) =>
        out.push({
          type: "preuve",
          key: "preuves",
          id: p.id,
          label: p.intitule,
          sub: p.statut,
        })
      );
    }

    const activitesMatch = activites.filter(
      (a) =>
        match(a.intitule, q) ||
        match(a.description, q) ||
        match(a.categorie, q)
    );
    if (activitesMatch.length > 0) {
      out.push({ type: "section", label: "Activités conformité" });
      activitesMatch.slice(0, 5).forEach((a) =>
        out.push({
          type: "activite",
          key: "referentiels",
          id: a.id,
          label: a.intitule,
          sub: a.categorie,
        })
      );
    }

    const siMatch = elementsSI.filter(
      (e) => match(e.nom, q) || match(e.type, q) || match(e.localisation, q)
    );
    if (siMatch.length > 0) {
      out.push({ type: "section", label: "Inventaire SI" });
      siMatch.slice(0, 5).forEach((e) =>
        out.push({
          type: "elementSI",
          key: "inventaire-si",
          id: e.id,
          label: e.nom,
          sub: e.type,
        })
      );
    }

    return out;
  }, [
    query,
    mode,
    personnes,
    traitements,
    sousTraitants,
    pointsCollecte,
    retraits,
    incidents,
    droits,
    proofs,
    activites,
    elementsSI,
  ]);

  const selectableResults = results.filter((r) => r.type !== "section");
  const selected = selectableResults[selectedIdx];

  const handleSelect = useCallback(
    (item) => {
      const searchTerm = query.trim() || null;
      if (item.type === "page") {
        onSelect(item.key, searchTerm ? { searchTerm } : undefined);
      } else if (item.key) {
        onSelect(item.key, { id: item.id, searchTerm, itemType: item.type });
      }
      onClose();
      setQuery("");
      setSelectedIdx(0);
    },
    [onSelect, onClose, query]
  );

  useEffect(() => {
    if (open) {
      setQuery("");
      setSelectedIdx(0);
      inputRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    setSelectedIdx(0);
  }, [query]);

  useEffect(() => {
    const down = (e) => {
      if (!open) return;

      if (e.key === "Escape") {
        e.preventDefault();
        onClose();
        return;
      }
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIdx((i) => Math.min(i + 1, selectableResults.length - 1));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIdx((i) => Math.max(i - 1, 0));
        return;
      }
      if (e.key === "Enter" && selected) {
        e.preventDefault();
        handleSelect(selected);
      }
    };

    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, [open, selected, selectableResults.length, handleSelect, onClose]);

  useEffect(() => {
    if (selected && listRef.current) {
      const el = listRef.current.querySelector(`[data-idx="${selectedIdx}"]`);
      el?.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIdx, selected]);

  if (!open) return null;

  return (
    <div
      className="ta-search-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      role="dialog"
      aria-modal="true"
      aria-label="Recherche RGPD"
    >
      <div className="ta-search-modal">
        <div className="ta-search-modal-input-wrap">
          <span className="ta-search-icon"><MdSearch size={16} /></span>
          <input
            ref={inputRef}
            type="text"
            className="ta-search-modal-input"
            placeholder="Rechercher pages, traitements, personnes, incidents..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "ArrowDown" || e.key === "ArrowUp" || e.key === "Enter") {
                e.preventDefault();
              }
            }}
          />
          <button
            type="button"
            className="ta-search-modal-close"
            onClick={onClose}
            aria-label="Fermer"
          >
            <MdClose size={20} aria-hidden />
          </button>
        </div>

        <div ref={listRef} className="ta-search-results">
          {results.length === 0 ? (
            <div className="ta-search-empty">
              {query.trim() ? "Aucun résultat" : "Tapez pour rechercher dans vos données RGPD"}
            </div>
          ) : (
            results.map((item, i) => {
              if (item.type === "section") {
                return (
                  <div key={`sec-${i}`} className="ta-search-section">
                    {item.label}
                  </div>
                );
              }
              const idx = selectableResults.indexOf(item);
              const isSelected = idx === selectedIdx;
              return (
                <button
                  key={`${item.type}-${item.id ?? item.key}-${i}`}
                  data-idx={idx}
                  type="button"
                  className={`ta-search-item ${isSelected ? "selected" : ""}`}
                  onClick={() => handleSelect(item)}
                  onMouseEnter={() => setSelectedIdx(idx)}
                >
                  {item.icon && <span className="ta-search-item-icon">{item.icon}</span>}
                  <div className="ta-search-item-content">
                    <div className="ta-search-item-label">{item.label}</div>
                    {item.sub && <div className="ta-search-item-sub">{item.sub}</div>}
                  </div>
                  {item.key && <span className="ta-search-item-page">{item.key}</span>}
                </button>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
